import React from "react";
import { useTranslation } from "react-i18next";

const MAPS_STREET_QUERY =
  "https://www.google.com/maps?q=Dorothea+Erxleben+Straße+4,+16321+Bernau+bei+Berlin";
const MAPS_CITY_QUERY =
  "https://www.google.com/maps?q=16321+Bernau+bei+Berlin";

/** Nur Ziffern und führendes + für zuverlässige tel:-Links auf Mobilgeräten */
function toTelHref(phone) {
  const normalized = phone.replace(/[^\d+]/g, "");
  if (normalized.startsWith("+")) {
    return `tel:${normalized}`;
  }
  return `tel:${normalized.replace(/^0+/, "")}`;
}

function ContactLinkRow({
  href,
  iconClass,
  label,
  value,
  external,
  title,
}) {
  return (
    <a
      href={href}
      title={title}
      className="group/link flex items-center gap-3 rounded-xl border border-slate-200/80 dark:border-slate-600/50 bg-slate-50/80 dark:bg-slate-900/30 px-3 py-3 text-left transition-all duration-200 hover:border-primary/25 dark:hover:border-primary-light/30 hover:bg-white dark:hover:bg-slate-800/70 hover:shadow-sm focus:outline-none focus-visible:ring-2 focus-visible:ring-primary/40 dark:focus-visible:ring-primary-light/40 focus-visible:ring-offset-2 focus-visible:ring-offset-white dark:focus-visible:ring-offset-slate-800"
      {...(external
        ? { target: "_blank", rel: "noopener noreferrer" }
        : {})}
    >
      <span
        className="flex h-10 w-10 flex-shrink-0 items-center justify-center rounded-xl bg-primary/10 text-primary transition-colors group-hover/link:bg-primary group-hover/link:text-white dark:bg-primary-light/15 dark:text-primary-light dark:group-hover/link:bg-primary-light dark:group-hover/link:text-white"
        aria-hidden
      >
        <i className={`${iconClass} text-sm`} />
      </span>
      <span className="min-w-0 flex-1">
        <span className="block truncate text-sm font-medium text-slate-900 dark:text-slate-100">
          {value}
        </span>
        <span className="mt-0.5 block text-xs font-medium text-slate-500 dark:text-slate-400">
          {label}
        </span>
      </span>
    </a>
  );
}

function Contact() {
  const { t } = useTranslation();
  const openMapsLabel = t("contact.open_in_maps");

  const people = [
    {
      id: "president",
      name: t("contact.contacts.0.name"),
      phone: t("contact.contacts.0.phone"),
      email: t("contact.contacts.0.email"),
      image: `${process.env.PUBLIC_URL}/images/dirk.webp`,
      role: t("contact.president"),
    },
    {
      id: "vice_president",
      name: t("contact.contacts.1.name"),
      phone: t("contact.contacts.1.phone"),
      email: t("contact.contacts.1.email"),
      image: `${process.env.PUBLIC_URL}/images/mike.webp`,
      role: t("contact.vicePresident"),
    },
  ];

  return (
    <section
      className="relative z-10 bg-white pt-12 pb-12 transition-colors dark:bg-slate-800"
      id="contact"
      aria-labelledby="contact-heading"
    >
      <div className="mx-auto max-w-7xl px-4 sm:px-6 lg:px-8">
        <header className="mb-10 text-center md:mb-12">
          <h2
            id="contact-heading"
            className="relative mb-5 inline-block text-4xl font-semibold tracking-tight text-primary dark:text-primary-light md:text-5xl"
          >
            {t("contact.title")}
            <span
              className="absolute -bottom-2.5 left-1/2 h-0.5 w-16 -translate-x-1/2 rounded-full bg-primary dark:bg-primary-light"
              aria-hidden
            />
          </h2>
          <p className="mx-auto mt-5 max-w-3xl text-pretty text-lg leading-relaxed text-slate-600 dark:text-slate-400">
            {t("contact.description")}
          </p>
        </header>

        <ul className="mb-10 grid list-none grid-cols-1 gap-5 p-0 md:grid-cols-2 xl:grid-cols-3 md:gap-6 m-0">
          {people.map((person) => (
            <li key={person.id} className="min-h-0">
              <article className="group/card flex h-full flex-col rounded-2xl border border-slate-200 bg-white p-6 shadow-sm transition-all duration-300 hover:shadow-md motion-safe:hover:-translate-y-0.5 dark:border-slate-700 dark:bg-slate-800 md:p-7">
                <div className="flex gap-4">
                  <img
                    src={person.image}
                    alt={t("contact.contactAlt", { name: person.name })}
                    width={96}
                    height={96}
                    loading="lazy"
                    decoding="async"
                    className="h-20 w-20 flex-shrink-0 rounded-2xl border-2 border-primary object-cover shadow-sm motion-safe:transition-transform motion-safe:group-hover/card:scale-[1.02] dark:border-primary-light md:h-24 md:w-24"
                  />
                  <div className="min-w-0 flex-1 pt-0.5">
                    <h3 className="text-lg font-semibold leading-snug text-slate-900 dark:text-slate-100 md:text-xl">
                      {person.name}
                    </h3>
                    <p className="mt-2 inline-flex items-center rounded-full border border-slate-200 bg-primary/5 px-2.5 py-1 text-xs font-semibold text-primary dark:border-slate-600 dark:bg-primary-light/10 dark:text-primary-light">
                      {person.role}
                    </p>
                    <p className="mt-2 text-xs leading-snug text-slate-500 dark:text-slate-400">
                      {t("contact.address.line1")}
                    </p>
                  </div>
                </div>

                <div className="mt-6 space-y-3 border-t border-slate-200 pt-6 dark:border-slate-700">
                  <ContactLinkRow
                    href={toTelHref(person.phone)}
                    iconClass="fas fa-phone"
                    label={t("contact.phone")}
                    value={person.phone}
                    title={person.phone}
                  />
                  <ContactLinkRow
                    href={`mailto:${person.email}`}
                    iconClass="fas fa-envelope"
                    label={t("contact.email")}
                    value={person.email}
                    title={person.email}
                  />
                </div>
              </article>
            </li>
          ))}

          <li key="address" className="min-h-0 md:col-span-2 xl:col-span-1">
            <article className="flex h-full flex-col rounded-2xl border border-slate-200 bg-white p-6 shadow-sm transition-all duration-300 hover:shadow-md motion-safe:hover:-translate-y-0.5 dark:border-slate-700 dark:bg-slate-800 md:p-7">
              <div className="flex gap-4">
                <img
                  src={`${process.env.PUBLIC_URL}/images/vereinsheim.webp`}
                  alt={t("contact.venue_image_alt")}
                  width={96}
                  height={96}
                  loading="lazy"
                  decoding="async"
                  className="h-20 w-20 flex-shrink-0 rounded-2xl border-2 border-primary object-cover shadow-sm dark:border-primary-light md:h-24 md:w-24"
                />
                <div className="min-w-0 flex-1 pt-0.5">
                  <h3 className="text-lg font-semibold leading-snug text-slate-900 dark:text-slate-100 md:text-xl">
                    {t("contact.address.title")}
                  </h3>
                  <p className="mt-2 inline-flex items-center rounded-full border border-slate-200 bg-primary/5 px-2.5 py-1 text-xs font-semibold text-primary dark:border-slate-600 dark:bg-primary-light/10 dark:text-primary-light">
                    {t("contact.headquaters")}
                  </p>
                  <p className="mt-2 text-xs leading-snug text-slate-500 dark:text-slate-400">
                    {t("contact.address.line1")}
                  </p>
                </div>
              </div>

              <div className="mt-6 space-y-3 border-t border-slate-200 pt-6 dark:border-slate-700">
                <ContactLinkRow
                  href={MAPS_STREET_QUERY}
                  iconClass="fas fa-road"
                  label={t("contact.street")}
                  value={t("contact.address.line2")}
                  external
                  title={openMapsLabel}
                />
                <ContactLinkRow
                  href={MAPS_CITY_QUERY}
                  iconClass="fas fa-map-marker-alt"
                  label={t("contact.place")}
                  value={t("contact.address.line3")}
                  external
                  title={openMapsLabel}
                />
              </div>
            </article>
          </li>
        </ul>

        <div className="overflow-hidden rounded-2xl border border-slate-200 bg-slate-50 shadow-sm dark:border-slate-700 dark:bg-slate-900/40">
          <div className="relative h-[min(55vw,380px)] min-h-[280px] w-full sm:h-[min(50vw,420px)] md:h-[min(45vw,480px)] md:min-h-[400px] lg:h-[480px]">
            <iframe
              src="https://www.google.com/maps/embed?pb=!1m18!1m12!1m3!1d2417.9594481199447!2d13.622852977935878!3d52.69682577210808!2m3!1f0!2f0!3f0!3m2!1i1024!2i768!4f13.1!3m3!1m2!1s0x47a9b6abf71102ed%3A0x58d1eb87dccf74fd!2sDorothea-Erxleben-Stra%C3%9Fe%204%2C%2016321%20Bernau%20bei%20Berlin!5e0!3m2!1sde!2sde!4v1732811482055!5m2!1sde!2sde"
              allowFullScreen
              loading="lazy"
              referrerPolicy="no-referrer-when-downgrade"
              title={t("contact.mapTitle")}
              className="absolute inset-0 h-full w-full border-0"
            />
          </div>
        </div>
      </div>
    </section>
  );
}

export default Contact;
